import firebase from 'firebase/app';
import 'firebase/storage';
import 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { UploadStatus } from '../../ui-components/catalogue-components/MenuUploadStatus';

export interface MenuUploadJob {
  id: string;
  status: UploadStatus;
  progress: number;
  fileUrls: string[];
  createdAt: firebase.firestore.Timestamp;
  updatedAt: firebase.firestore.Timestamp;
  errorMessage?: string;
}

export const uploadMenuFiles = async (
  userId: string,
  files: File[]
): Promise<string> => {
  try {
    // Create a unique ID for this upload job
    const jobId = uuidv4();
    // Create a reference to the upload job in Firestore
    const menuUploadRef = firebase
      .firestore()
      .collection('companies')
      .doc(userId)
      .collection('catalogue')
      .doc('catalogue')
      .collection('menuUploads')
      .doc(jobId);

    // Initialize the job in Firestore - explicitly set status to 'uploading'
    await menuUploadRef.set({
      id: jobId,
      status: 'uploading' as UploadStatus,
      progress: 0,
      fileUrls: [],
      createdAt: firebase.firestore.FieldValue.serverTimestamp(),
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });

    // Upload each file to Firebase Storage
    const fileUrls: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileId = uuidv4();
      const fileRef = firebase
        .storage()
        .ref(`${userId}/menuUploads/${jobId}/${fileId}_${file.name}`);

      // Upload the file
      const uploadTask = fileRef.put(file);

      // Wait for the upload to complete
      await uploadTask;

      // Get the download URL
      const downloadUrl = await fileRef.getDownloadURL();
      fileUrls.push(downloadUrl);

      // Update progress
      await menuUploadRef.update({
        progress: Math.round(((i + 1) / files.length) * 100),
        fileUrls: firebase.firestore.FieldValue.arrayUnion(downloadUrl),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });
    }

    // CRITICAL CHANGE: Now that all files are uploaded, set status to 'processing'
    // This status change will trigger the server-side processing
    await menuUploadRef.update({
      status: 'processing' as UploadStatus,
      updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
    });

    // Return the job ID so we can track its status
    return jobId;
  } catch (error) {
    console.error('Error uploading menu files:', error);
    throw error;
  }
};

export const getMenuUploadStatus = async (
  userId: string,
  jobId: string
): Promise<MenuUploadJob | null> => {
  try {
    const menuUploadRef = firebase
      .firestore()
      .collection('companies')
      .doc(userId)
      .collection('catalogue')
      .doc('catalogue')
      .collection('menuUploads')
      .doc(jobId);

    const doc = await menuUploadRef.get();

    if (doc.exists) {
      return doc.data() as MenuUploadJob;
    }

    return null;
  } catch (error) {
    console.error('Error getting menu upload status:', error);
    throw error;
  }
};

/**
 * Checks for any active menu upload jobs and returns the most recent one if found
 */
export const checkForActiveUploads = async (
  userId: string
): Promise<MenuUploadJob | null> => {
  try {
    const menuUploadsRef = firebase
      .firestore()
      .collection('companies')
      .doc(userId)
      .collection('catalogue')
      .doc('catalogue')
      .collection('menuUploads')
      .orderBy('createdAt', 'desc')
      .limit(1);

    const snapshot = await menuUploadsRef.get();

    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      const jobData = doc.data() as MenuUploadJob;

      // Only return jobs that are still in progress or were recently completed/failed
      if (
        [
          'uploading',
          'processing',
          'analyzing',
          'generating',
          'completed',
          'failed',
        ].includes(jobData.status)
      ) {
        // For completed or failed jobs, only return them if they're recent (within the last 10 minutes)
        if (jobData.status === 'completed' || jobData.status === 'failed') {
          const jobTime = jobData.updatedAt?.toDate();
          const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);

          if (!jobTime || jobTime < tenMinutesAgo) {
            return null;
          }
        }

        return {
          ...jobData,
          id: doc.id,
          // Ensure Firestore timestamps are converted to proper objects
          createdAt: jobData.createdAt,
          updatedAt: jobData.updatedAt,
        };
      }
    }

    return null;
  } catch (error) {
    console.error('Error checking for active uploads:', error);
    return null;
  }
};

export const subscribeToMenuUploadStatus = (
  userId: string,
  jobId: string,
  callback: (job: MenuUploadJob) => void
): (() => void) => {
  const menuUploadRef = firebase
    .firestore()
    .collection('companies')
    .doc(userId)
    .collection('catalogue')
    .doc('catalogue')
    .collection('menuUploads')
    .doc(jobId);

  const unsubscribe = menuUploadRef.onSnapshot((doc) => {
    if (doc.exists) {
      callback(doc.data() as MenuUploadJob);
    }
  });

  return unsubscribe;
};
